import { DeveloperProfileConstants } from "sj-ui-toolkit/src/constants/developerProfile";
import { AI_TOOL_FIELDS } from "../constants/profileDetailsFields";

/** Shape of the user object accessed by this helper. */
interface UserProfile {
  email?: string;
  name?: {
    givenName?: string;
    familyName?: string;
  };
  attributes?: {
    company?: string;
    title?: string;
    country?: string;
    jobRole?: string;
  };
  developerProfile?: {
    jobFunction?: string;
    experience?: string;
    expertise?: string;
    language?: string;
    additionalLanguages?: string[];
    topics?: string[];
    usesAi?: boolean;
    aiStack?: Record<string, string[]>;
  };
}

/**
 * Form data shape used across profile edit forms. AI tool fields (aiModels,
 * aiAssistants, etc.) are addressed by dynamic keys, hence the index signature.
 */
export interface ProfileFormData {
  email: string;
  firstName: string;
  lastName: string;
  company: string;
  title: string;
  country: string;
  role: string;
  function: string;
  functionOther?: string;
  experience: string;
  expertise: string;
  language: string;
  languageOther?: string;
  additionalLanguages: string[];
  tooling: string[] | string;
  toolingOther?: string;
  doesNotUseAi?: boolean;
  [key: string]: unknown;
}

export const formatDeveloperProfile = (user: UserProfile): ProfileFormData => {
  return {
    firstName: user?.name?.givenName || "",
    lastName: user?.name?.familyName || "",
    company: user?.attributes?.company || "",
    title: user?.attributes?.title || "",
    country: user?.attributes?.country || "",
    email: user?.email || "",
    role: user?.attributes?.jobRole || "",
    function: user?.developerProfile?.jobFunction || "",
    experience: user?.developerProfile?.experience || "",
    expertise: user?.developerProfile?.expertise || "",
    language: user?.developerProfile?.language || "",
    additionalLanguages: user?.developerProfile?.additionalLanguages || [],
    tooling: user?.developerProfile?.topics || [],
  };
};

const normalizeTopics = (tooling: string[] | string | null | undefined): string[] => {
  if (Array.isArray(tooling)) return tooling;
  if (!tooling) return [];

  return String(tooling)
    .split(",")
    .map(t => t.trim())
    .filter(Boolean);
};

const buildTopicsPayload = (
  tooling: string[] | string | undefined,
  toolingOther: string | undefined
): string[] => {
  const selected = normalizeTopics(tooling);

  if (selected.includes("other") && toolingOther) {
    return [...selected, toolingOther];
  }

  return selected;
};

const hasAiSelections = (formData: ProfileFormData): boolean =>
  AI_TOOL_FIELDS.some(field => ((formData[field.key] as string[] | undefined) || []).length > 0);

export const buildUserProfilePayload = (formData: ProfileFormData) => {
  const language = formData.language === "other" ? formData.languageOther : formData.language;
  const jobFunction = formData.function === "other" ? formData.functionOther : formData.function;

  return {
    email: formData.email,
    name: {
      givenName: formData.firstName,
      familyName: formData.lastName,
    },
    attributes: {
      jobRole: formData.role,
      title: formData.title,
      country: formData.country,
      company: formData.company,
    },
    developerProfile: {
      experience: formData.experience,
      expertise: formData.expertise,
      jobFunction: jobFunction,
      language: language,
      additionalLanguages: formData.additionalLanguages,
      topics: buildTopicsPayload(formData.tooling, formData.toolingOther),
      // usesAi stays unset when the user has neither opted out nor selected anything
      usesAi: formData.doesNotUseAi ? false : hasAiSelections(formData) ? true : undefined,
      // only include aiStack when there is meaningful data to send; omitting it
      // leaves existing server selections intact (initial/unanswered state)
      ...(formData.doesNotUseAi || hasAiSelections(formData)
        ? {
            aiStack: Object.fromEntries(
              AI_TOOL_FIELDS.map(field => [
                field.categoryKey,
                (formData[field.key] as string[] | undefined) || [],
              ])
            ),
          }
        : {}),
    },
  };
};

/**
 * Maps the developer profile's aiStack object onto the categorized AI tool
 * form fields, plus the "does not use AI" opt-out.
 */
export const normalizeAiToolSelections = (
  developerProfile: UserProfile["developerProfile"]
): Record<string, unknown> => {
  const aiStack = developerProfile?.aiStack || {};
  const selections = Object.fromEntries(
    AI_TOOL_FIELDS.map(field => [field.key, aiStack[field.categoryKey] || []])
  );

  // usesAi is only false when the user explicitly opted out; unset means unanswered
  return { doesNotUseAi: developerProfile?.usesAi === false, ...selections };
};

/**
 * Normalizes a single-select field value that may be a custom "other" entry.
 *
 * When the user selects "Other" and types a custom value, the API stores the
 * raw custom text (not the string "other"). On load we check if the value is a
 * known option; if not, we treat it as a custom other value.
 */
export const normalizeFieldWithOther = (
  value: string,
  validOptionValues: string[]
): { field: string; fieldOther: string } => {
  if (!value) {
    return { field: "", fieldOther: "" };
  }

  // Sentinel stored when the user picks "Other" but hasn't typed a custom value
  if (value === "other") {
    return { field: "other", fieldOther: "" };
  }

  if (validOptionValues.includes(value)) {
    return { field: value, fieldOther: "" };
  }

  // Unknown value — the API stored a custom free-text entry
  return { field: "other", fieldOther: value };
};

/**
 * Normalizes the topics array returned from API into separate UI values.
 *
 * tooling: Valid MultiSelect values (plus *other* when selected)
 * toolingOther: Custom user entered value (if any)
 */
export const normalizeToolingWithOther = (
  topics: string[] = []
): { tooling: string[]; toolingOther: string } => {
  const validOptions = DeveloperProfileConstants.OPTIONS.architectureTools.map(
    (opt: { value: string }) => opt.value
  );

  const knownValues: string[] = [];
  let toolingOther = "";
  let hasOther = false;

  topics.forEach(item => {
    if (item === "other") {
      hasOther = true;
    } else if (validOptions.includes(item)) {
      knownValues.push(item);
    } else if (!toolingOther) {
      toolingOther = item;
    }
  });

  const tooling = hasOther ? [...knownValues, "other"] : knownValues;

  return {
    tooling,
    toolingOther,
  };
};
